<template>
  <div data-private>
  <PageHeader
    title="Coding"
    subtitle="Manage patient codes, suspect rules, and coding operations"
  >
    <template #bottom>
      <Page_Tabs
        :tabs="tabOptions"
        :initial-tab="activeTab"
        class="px-6 pt-4"
        no-border
      />
    </template>
  </PageHeader>

  <div class="p-6">
    <!-- Patient Codes Tab -->
    <div v-if="activeTab === 'Patient Codes'" class="bg-white rounded border border-gray-200">
      <Worklist_FilterBar
        storage-key="coding_entity_filter"
        :info-tag-types="[{ type: 'Care Gap Practice Access', label: 'EMR Access', color: 'blue' }]"
        @filter-changed="onPatientsFilterChanged"
      />
      <Coding_Patient_Codes :entity-filters="entityFilters" :infotag-filters="infotagFilters" />
    </div>

    <!-- Suspect Rules Tab -->
    <div v-if="activeTab === 'Suspect Rules'" class="bg-white rounded border border-gray-200">
      <SuspectRulesTab
        :attributes="attributes"
        :entity-filters="entityFilters"
      />
    </div>

    <!-- Summary Tab (superuser only — cleared 2026-07-27, was the v1 report) -->
    <div v-if="activeTab === 'Summary (beta)'" class="bg-white rounded border border-gray-200">
      <Coding_Summary />
    </div>

    <!-- Operations V2 (beta) Tab -->
    <div v-if="activeTab === 'Operations V2 (beta)'" class="bg-white rounded border border-gray-200">
      <Coding_Operations_V2 />
    </div>

    <!-- Assignments Tab -->
    <div v-if="activeTab === 'Assignments'" class="bg-white rounded border border-gray-200">
      <Worklist_Assignments
        :metric-map="{ 'PVP': 34, 'Foundations': 50 }"
        :member-entity-types="['Coding Analyst', 'Coding Manager']"
        member-label="Assignment"
        stage-label="Tier"
        :task-type-map="codingTaskTypeMap"
        :hide-role="true"
        :show-queue-load="true"
      />
    </div>

    <!-- Console Tab -->
    <div v-if="activeTab === 'Console (beta)'" class="bg-white rounded border border-gray-200">
      <Coding_Console />
    </div>

    <!-- Program Economics Tab -->
    <div v-if="activeTab === 'Program Economics (beta)'" class="bg-white rounded border border-gray-200">
      <Coding_Program_Economics />
    </div>
  </div>
  </div>
</template>

<script>
import axios from 'axios';
import Coding_Patient_Codes from '@/components/Coding/Coding_Patient_Codes.vue';
import Coding_Summary from '@/components/Coding/Coding_Summary.vue';
import Coding_Operations_V2 from '@/components/Coding/Coding_Operations_V2.vue';
import Coding_Console from '@/components/Coding/Coding_Console.vue';
import Coding_Program_Economics from '@/components/Coding/Coding_Program_Economics.vue';
import SuspectRulesTab from './SuspectRulesTab.vue';
import Worklist_Assignments from '@/components/widgetcomponents/Worklist_Assignments.vue';
import Worklist_FilterBar from '@/components/widgetcomponents/Worklist_FilterBar.vue';
import PageHeader from '@/components/widgetcomponents/library/PageHeader.vue';
import Page_Tabs from '@/components/widgetcomponents/Page_Tabs.vue';
import { canAccessFeature } from '@/utils/access';
import { FEATURES } from '@/constants/features';

export default {
  name: 'Coding_Overview',
  components: {
    Coding_Patient_Codes,
    Coding_Summary,
    Coding_Operations_V2,
    Coding_Console,
    Coding_Program_Economics,
    SuspectRulesTab,
    Worklist_Assignments,
    Worklist_FilterBar,
    PageHeader,
    Page_Tabs,
  },
  data() {
    return {
      attributes: [],
      entityFilters: null,
      infotagFilters: null,
      codingTaskTypeMap: [
        { program: 'PVP', tier: '1LR', id: 1 },
        { program: 'PVP', tier: '2LR', id: 2 },
        { program: 'PVP', tier: '3LR', id: 3 },
        { program: 'PVP', tier: 'Confirm', id: 4 },
        { program: 'Foundations', tier: '1LR', id: 10 },
        { program: 'Foundations', tier: '2LR', id: 11 },
        { program: 'Foundations', tier: '3LR', id: 12 },
        { program: 'Foundations', tier: 'Confirm', id: 13 },
      ],
    };
  },
  computed: {
    tabOptions() {
      const tabs = [
        { label: 'Patient Codes', icon: ['fas', 'users'], to: '/coding/patients' },
        { label: 'Suspect Rules', icon: ['fas', 'magnifying-glass-chart'], to: '/coding/suspect-rules' },
        { label: 'Assignments', icon: ['fas', 'users-cog'], to: '/coding/assignments' },
        { label: 'Operations V2 (beta)', icon: ['fas', 'chart-bar'], to: '/coding/operations-v2' },
        { label: 'Summary (beta)', icon: ['fas', 'chart-bar'], to: '/coding/summary' },
        { label: 'Console (beta)', icon: ['fas', 'headset'], to: '/coding/console' },
      ];
      // Beta, superuser/granted-admin only (see migration 0613 + access.js override).
      if (canAccessFeature(FEATURES.COMPONENT_CODING_ECONOMICS)) {
        tabs.push({ label: 'Program Economics (beta)', icon: ['fas', 'chart-line'], to: '/coding/economics' });
      }
      return tabs;
    },
    activeTab() {
      const path = this.$route.path;
      if (path.startsWith('/coding/suspect-rules')) return 'Suspect Rules';
      if (path.startsWith('/coding/operations-v2')) return 'Operations V2 (beta)';
      if (path.startsWith('/coding/summary')) return 'Summary (beta)';
      if (path.startsWith('/coding/assignments')) return 'Assignments';
      if (path.startsWith('/coding/console')) return 'Console (beta)';
      if (path.startsWith('/coding/economics')) return 'Program Economics (beta)';
      return 'Patient Codes';
    },
  },
  methods: {
    async fetchAttributes() {
      try {
        const response = await axios.get('api/impact_analytics/attributes/');
        this.attributes = response.data;
      } catch (error) {
        console.error('Error fetching attributes:', error);
      }
    },
    onPatientsFilterChanged(filters) {
      this.entityFilters = filters.entity_filters;
      this.infotagFilters = filters.infotag_filters || null;
    },
    onFilterChanged(filters) {
      this.entityFilters = filters.entity_filters;
      this.infotagFilters = filters.infotag_filters || null;
    },
  },
  mounted() {
    this.fetchAttributes();
  },
};
</script>
